import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import CountryDropdown from "../../countryDropDown";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

const EditContactDialog = ({
  visible,
  onHide,
  onSave,
  isAddMode,
  rowToEdit,
  isLoading,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: rowToEdit?.name || "",
      other_name: rowToEdit?.other_name || "",
      preferred_name: rowToEdit?.preferred_name || "",
      last_name: rowToEdit?.last_name || "",
      role: rowToEdit?.role || "",
      email: rowToEdit?.email || "",
      country: rowToEdit?.country || "",
    },
  });

  useEffect(() => {
    reset({
      name: rowToEdit?.name || "",
      last_name: rowToEdit?.last_name || "",
      other_name: rowToEdit?.other_name || "",
      preferred_name: rowToEdit?.preferred_name || "",

      role: rowToEdit?.role || "",
      email: rowToEdit?.email || "",
      country: rowToEdit?.country || "",
      phone_code: rowToEdit?.phone_code || "",
      phone: rowToEdit?.phone || "",
    });
  }, [rowToEdit, reset]);

  const onSubmit = async (data) => {
    let cont = {
      ...data,
      country: data?.country?.code,
      phone: data?.phone ? data?.phone : "",
      phone_code: data?.phone_code ? data?.phone_code : "",
    };
    await onSave(cont);
    reset({
      name: "",
      last_name: "",
      preferred_name: "",
      other_name: "",
      role: "",
      email: "",
      country: "",
      phone_code: "",
      phone: "",
    });
  };

  const handleSkip = () => {
    reset({
      name: "",
      last_name: "",
      preferred_name: "",
      other_name: "",
      role: "",
      email: "",
      country: "",
      phone_code: "",
      phone: "",
    });
    onHide();
  };

  return createPortal(
    <Dialog visible={visible} onHide={onHide} className="edit-contact-dialog">
      <form onSubmit={handleSubmit(onSubmit)} className="form-container">
        <p className="del-dialog-heading">
          {isAddMode ? "Add New Contact" : "Edit Contact"}
        </p>
        <p className="del-dialog-para mb-3">
          {isAddMode
            ? "Fill in the new contact's information below and hit add contact when ready!"
            : "Change the contact information and hit save changes when ready!"}
        </p>

        <div className="formgrid grid">
          <div className="field col-6">
            <label className="contact-label mt-2">
              Given Name(s) *{" "}
              <span id="given-name-info" className="pi pi-info-circle"></span>
              <Tooltip
                target="#given-name-info"
                content="This is usually the person's first name or the name they usually go by."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              rules={{ required: "First Name is required." }}
              render={({ field }) => (
                <InputText
                  {...field}
                  type="text"
                  placeholder="Enter First Name"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.name ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.name && (
              <small className="error-message">{errors.name.message}</small>
            )}
          </div>

          <div className="field col-6">
            <label className="contact-label mt-2">
              Other Name(s){" "}
              <span id="other-name-info" className="pi pi-info-circle"></span>
              <Tooltip
                target="#other-name-info"
                content="Any middle or extra names that the employee has. You can leave this blank if it doesn’t apply."
                position="right"
                className="tooltip-custom-format"
              />
            </label>
            <Controller
              name="other_name"
              control={control}
              defaultValue
              rules={{
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Special characters are not allowed.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  type="text"
                  placeholder="Enter any other names, such as middle names or clan names"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.other_name ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.other_name && (
              <small className="error-message">
                {errors.other_name.message}
              </small>
            )}
          </div>

          <div className="field col-6">
            <label className="contact-label mt-2">
              Surname(s)
              <span
                id="surname-info"
                className="pi pi-info-circle info-circle-span"
              ></span>
              <Tooltip
                target="#surname-info"
                content="This is usually a family name or last name. If employee has more than one, include them here."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <Controller
              name="last_name"
              control={control}
              defaultValue=""
              rules={{
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Special characters are not allowed.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  type="text"
                  placeholder="Enter surname(s)."
                  className={`input-field appearance-none outline-none w-full ${
                    errors.last_name ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.last_name && (
              <small className="error-message">
                {errors.last_name.message}
              </small>
            )}
          </div>

          <div className="field col-6">
            <label className="contact-label mt-2">
              Preferred Name(s)
              <span
                id="preferred-name-info"
                className="pi pi-info-circle info-circle-span"
              ></span>
              <Tooltip
                target="#preferred-name-info"
                content="If the employee wants to be called by a different name than their given name, put that here."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <Controller
              name="preferred_name"
              control={control}
              defaultValue=""
              rules={{
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Special characters are not allowed.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  type="text"
                  placeholder="Preferred name or nickname"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.preferred_name ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.preferred_name && (
              <small className="error-message">
                {errors.preferred_name.message}
              </small>
            )}
          </div>

          <div className="field col-12">
            <label className="contact-label">Role*</label>
            <Controller
              name="role"
              control={control}
              defaultValue=""
              rules={{ required: "Role is required." }}
              render={({ field }) => (
                <InputText
                  {...field}
                  type="text"
                  placeholder="Enter Role"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.role ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.role && (
              <small className="error-message">{errors.role.message}</small>
            )}
          </div>
          <div className="field col-12">
            <label className="contact-label">Contact Email*</label>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              rules={{
                required: "Email is required.",
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "Email is invalid.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  type="text"
                  placeholder="Enter Email Address"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.email ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.email && (
              <small className="error-message">{errors.email.message}</small>
            )}
          </div>
          <div className="field col-12">
            <label className="contact-label">Country*</label>
            <Controller
              name="country"
              control={control}
              defaultValue=""
              rules={{ required: "Country is required." }}
              render={({ field }) => (
                <CountryDropdown
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  error={errors.country}
                />
              )}
            />
            {errors.country && (
              <small className="error-message">{errors.country.message}</small>
            )}
          </div>
          <div className="field col-12 mb-0">
            <label className="contact-label">Phone Number</label>
          </div>
          <div className="field col-3">
            <Controller
              name="phone_code"
              control={control}
              defaultValue=""
              rules={{
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Phone code must contain only digits.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  type="text"
                  placeholder="Country Code"
                  className={`input-field appearance-none outline-none w-full${
                    errors.phone_code ? "" : ""
                  }`}
                />
              )}
            />
            {errors.phone_code && (
              <small className="error-message">
                {errors.phone_code.message}
              </small>
            )}
          </div>
          <div className="field col-9">
            <Controller
              name="phone"
              control={control}
              defaultValue=""
              rules={{
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Phone number must contain only digits.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  type="text"
                  placeholder="Enter Phone Number"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.phone ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.phone && (
              <small className="error-message">{errors.phone.message}</small>
            )}
          </div>
        </div>

        <div className="flex justify-content-end gap-2 mt-4">
          <button type="button" className="cancel-btns" onClick={handleSkip}>
            Cancel
          </button>
          <Button
            loading={isLoading}
            type="submit"
            className={`add-new-contact add-contact ${!isValid ? "disable" : ""}`}
            disabled={!isValid}
          >
            {!isLoading && (isAddMode ? "Add Contact" : "Save Changes")}
          </Button>
        </div>
      </form>
    </Dialog>,
    document.getElementById("portal-root"),
  );
};
EditContactDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isAddMode: PropTypes.bool,
  rowToEdit: PropTypes.object,
};
export default EditContactDialog;
